import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../Reducers/store';
import {
  getMeetingRequest,
  confirmMeeting,
  cancelMeetingRequest,
  rsvpMeeting,
  rsvpBadgeClass,
  rsvpLabel,
} from '../../Api/ScheduleApi';
import type { MeetingRequestDetails } from '../../Api/ScheduleApi';
import Loader from '../Common/Loader';
import { toast } from 'react-toastify';
import { trackEvent } from '../../Utils/analytics';
import { formatInstantRange } from '../../Utils/timeZone';

export default function MeetingInvitePage() {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const { userModel } = useAppSelector((s) => s.user);
  const [request, setRequest] = useState<MeetingRequestDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = (id: number) =>
    getMeetingRequest(id)
      .then((data) => setRequest(data))
      .catch(() => {
        toast.error('Meeting request not found or you do not have access.');
        navigate('/');
      });

  useEffect(() => {
    const id = requestId ? parseInt(requestId, 10) : NaN;
    if (!id || isNaN(id)) {
      setLoading(false);
      return;
    }
    getMeetingRequest(id)
      .then((data) => {
        setRequest(data);
        trackEvent('meeting_invite_viewed', {
          request_id: data.id,
          status: data.status,
          entry: 'portal_authenticated',
        });
      })
      .catch(() => {
        toast.error('Meeting request not found or you do not have access.');
        navigate('/');
      })
      .finally(() => setLoading(false));
  }, [requestId, navigate]);

  const handleAccept = async () => {
    const id = requestId ? parseInt(requestId, 10) : NaN;
    if (!id || isNaN(id) || !request) return;
    setSubmitting(true);
    try {
      if (request.status === 0) {
        await confirmMeeting(id);
      } else if (request.meetingId) {
        await rsvpMeeting(request.meetingId, true);
      }
      trackEvent('meeting_request_accepted', {
        request_id: id,
        entry: 'portal_authenticated',
      });
      toast.success('RSVP accepted!');
      await load(id);
    } catch {
      toast.error('Unable to accept the meeting.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    const id = requestId ? parseInt(requestId, 10) : NaN;
    if (!id || isNaN(id) || !request) return;
    if (!window.confirm('Decline this meeting invite?')) return;
    setSubmitting(true);
    try {
      if (request.meetingId && request.status !== 0) {
        await rsvpMeeting(request.meetingId, false);
      } else {
        await cancelMeetingRequest(id);
      }
      trackEvent('meeting_request_declined', {
        request_id: id,
        entry: 'portal_authenticated',
      });
      toast.success('RSVP declined.');
      await load(id);
    } catch {
      toast.error('Unable to decline the meeting.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Loader />;
  if (!request) return null;

  const myParticipant = request.participants?.find((p) => p.userId === userModel?.id);
  const isInvitee =
    userModel?.id === request.recipientUserId ||
    (!!myParticipant && myParticipant.role !== 'organizer');
  const myPending =
    isInvitee &&
    (request.myRsvpStatus === 'pending' ||
      (!request.myRsvpStatus && request.status === 0) ||
      myParticipant?.status === 'pending');

  return (
    <div className="max-w-xl mx-auto p-6">
      <h1 className="text-xl font-bold text-gray-900 mb-4">Meeting Request</h1>
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
        <p>
          <span className="font-medium">From:</span> {request.requesterName}
          {request.requesterMemberName ? ` (${request.requesterMemberName})` : ''}
        </p>
        <p>
          <span className="font-medium">Time:</span>{' '}
          {formatInstantRange(request.startUtc, request.endUtc, request.timeZoneId)}
        </p>
        {request.message ? (
          <p>
            <span className="font-medium">Details:</span> {request.message}
          </p>
        ) : null}
        {request.participants && request.participants.length > 0 ? (
          <div className="pt-2 border-t border-gray-200">
            <p className="font-medium mb-2">Participants</p>
            <ul className="space-y-2">
              {request.participants.map((p) => (
                <li key={`${p.role}-${p.userId}`} className="flex items-center justify-between gap-2">
                  <span>
                    {p.name || `User ${p.userId}`}
                    {p.memberName ? (
                      <span className="text-gray-500 text-sm"> · {p.memberName}</span>
                    ) : null}
                  </span>
                  <span
                    className={`text-xs font-semibold px-2 py-0.5 rounded ${rsvpBadgeClass(
                      p.status,
                      p.role
                    )}`}
                  >
                    {rsvpLabel(p.status, p.role)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
      {myPending ? (
        <div className="mt-6 flex gap-4">
          <button
            type="button"
            onClick={handleAccept}
            disabled={submitting}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
          >
            {submitting ? 'Processing...' : 'Accept'}
          </button>
          <button
            type="button"
            onClick={handleDecline}
            disabled={submitting}
            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
          >
            Decline
          </button>
        </div>
      ) : (
        <p className="mt-4 text-gray-600">
          {request.myRsvpStatus === 'accepted' || myParticipant?.status === 'accepted'
            ? 'You have accepted this meeting.'
            : null}
          {request.myRsvpStatus === 'declined' || myParticipant?.status === 'declined'
            ? 'You have declined this meeting.'
            : null}
          {request.status === 3 ? 'This meeting request was cancelled.' : null}
        </p>
      )}
    </div>
  );
}
